/**
 * The example slug every flows description, error and schema cites. ONE copy,
 * because the 2026-09-17 flare was a stale example (`notion.com`, never
 * public) repeated in six places. Ours, so no third party can un-publish it;
 * `tests/flows-api.test.ts` pins that nothing else plays the part.
 */
export const FLOW_EXAMPLE_SLUG = 'brew.new'

/** A flow needs more than two brand emails to be worth publishing. */
export const MIN_FLOW_STEPS = 3

/** Bounded so a flow row stays a small document and a canvas stays legible. */
export const MAX_FLOW_STEPS = 12

/**
 * Send streams one collection may hold. The stream lane never opens more
 * (past it, an email joins the most recently active stream, marked for
 * review), so every read of a collection's streams takes them all.
 */
export const MAX_STREAMS_PER_COLLECTION = 200

/** Deliveries read per enrollment when picking steps (one bounded index range). */
export const FLOW_CANDIDATE_SCAN_CAP = 200

/** Thumbnails denormalized onto a gallery card. */
export const FLOW_CARD_THUMBNAIL_COUNT = 3

/**
 * Public flows served to the gallery grid in one read. The gallery holds
 * every public flow (its search matches the brand over the loaded cards),
 * so this bounds the read, not the page: a flow row is a few kilobytes and
 * the read is cached for an hour.
 */
export const FLOW_LIST_CAP = 500

export const DAY_MS = 86_400_000

/**
 * A signup mark that precedes the sequence's first email by more than this
 * is not this sequence's start (a legacy merge carried it); the offsets
 * then count from the first email. The recorded form submission is exempt.
 */
export const FLOW_ANCHOR_MAX_LEAD_DAYS = 7

/** A `publishing` flow older than this reads as stalled in the admin. */
export const FLOW_PUBLISH_STALL_MS = 15 * 60_000

export const FLOW_TEMPLATE_FLOW_TYPES = ['newsletter', 'signup'] as const
export type FlowTemplateFlowType = (typeof FLOW_TEMPLATE_FLOW_TYPES)[number]

/**
 * Auto-publish: what a collection must show before the flows page takes
 * its brand without an operator. Stricter than the manual floor on purpose —
 * an operator can publish three emails they have looked at; the automation
 * needs a sequence that is plainly a marketing series.
 */
/** "Lots of emails": at least this many sequence emails survive selection. */
export const AUTO_PUBLISH_MIN_STEPS = 4
/** "Marketing based": this share of the steps carry a marketing category. */
export const AUTO_PUBLISH_MIN_MARKETING_SHARE = 0.6
/** A burst sent within a day is notifications, not a sequence. */
export const AUTO_PUBLISH_MIN_SPAN_DAYS = 1
/**
 * "A sequence, not a recurring digest": MORE than half the steps must carry a
 * distinct subject. A weekly report re-sent under one subject clears every
 * other bar — it is marketing-shaped, spans months, and authenticates — but
 * twelve copies of "Weekly Admin Digest" is not a flow anyone can remix.
 * `selectFlowSteps` cannot catch it either: its `duplicate` rule keys on
 * `contentFingerprint`, and each week's body genuinely differs.
 */
export const AUTO_PUBLISH_MIN_SUBJECT_VARIETY = 0.5
/** New brands the cron publishes per tick (each step drives a Playwright render). */
export const FLOW_AUTO_PUBLISH_MAX_PER_RUN = 5
/** Wall-clock budget of one tick; no new publish starts past it. */
export const FLOW_AUTO_PUBLISH_BUDGET_MS = 10 * 60_000
/** Enrollments read per candidate page. */
export const FLOW_AUTO_PUBLISH_PAGE_SIZE = 25
/**
 * Streams past their collection's first window evaluated per tick, one
 * query each (`getStreamAutoPublishCandidate`). A stream whose own flow is
 * ready never counts (the reconcile lane keeps it current); the window
 * rotates per tick, so every other stream is read in turn.
 */
export const FLOW_AUTO_PUBLISH_MAX_DEFERRED_READS = 100
/** The share of the discovery lane's time those reads may spend, so the publishes keep the rest. */
export const FLOW_AUTO_PUBLISH_DEFERRED_READ_SHARE = 0.5
/** The cron's cadence: the window of deferred streams read moves on once per tick. */
export const FLOW_AUTO_PUBLISH_TICK_MS = 60 * 60_000

/**
 * Keeping a published flow tied to its collection: every tick reconciles
 * each live flow with what the collection holds now — appends the emails
 * the brand sent since (up to the cap), drops a step a newer rule excludes,
 * publishes the emails past the cap as gallery templates.
 */
/** Published flows read per reconcile page. */
export const FLOW_SYNC_PAGE_SIZE = 25
/**
 * The share of a tick the reconcile lane may spend before the determiner's
 * new brands get their turn. Reconciling is open-ended — a brand that sent
 * five emails since the last tick costs five conversions — so without a
 * ceiling a busy hour would spend the whole tick keeping live flows current
 * and never publish a new brand again. What the lane does not finish it
 * continues next tick.
 */
export const FLOW_SYNC_BUDGET_SHARE = 0.7
/** A step whose conversion failed this many times waits for an operator. */
export const FLOW_SYNC_MAX_ATTEMPTS = 3

/**
 * Emails one removal request may name. The removal mutation still works
 * through them one transaction-sized batch at a time (an `emails` row can
 * be a megabyte); this bounds what one confirmation can take away.
 */
export const MAX_FLOW_EMAIL_REMOVAL_BATCH = 12
